
import React, { useState } from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { HelpCircle, TicketIcon, MessageSquare, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MOCK_FAQS } from '@/utils/mockData';
import { FAQ } from '@/types';
import TicketSubmissionForm from '@/components/support/TicketSubmissionForm';

const HelpCenter: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('faq');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter FAQs by search query
  const filteredFAQs = MOCK_FAQS.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group FAQs by category
  const faqsByCategory: Record<string, FAQ[]> = {};
  filteredFAQs.forEach(faq => {
    if (!faqsByCategory[faq.category]) {
      faqsByCategory[faq.category] = [];
    }
    faqsByCategory[faq.category].push(faq);
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <MainLayout allowedRoles={['author']}>
      <PageHeader 
        title="Help Center" 
        description="Find answers to common questions or get support from our team"
      />
      
      <Tabs defaultValue="faq" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 mb-8">
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            <span>FAQ</span>
          </TabsTrigger>
          <TabsTrigger value="ticket" className="flex items-center gap-2">
            <TicketIcon className="h-4 w-4" />
            <span>Submit Ticket</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            <span>Contact Us</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="faq">
          <Card>
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
              <CardDescription>
                Browse common questions about royalties, payments, and account management
              </CardDescription>
              <div className="relative mt-4">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search FAQs..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
            </CardHeader>
            <CardContent>
              {Object.keys(faqsByCategory).length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  No FAQs match your search. Try different keywords or submit a ticket.
                </p>
              )}
              
              {Object.entries(faqsByCategory).map(([category, faqs]) => (
                <div key={category} className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 capitalize">{category} Questions</h3>
                  <Accordion type="single" collapsible className="w-full">
                    {faqs.map((faq) => (
                      <AccordionItem key={faq.id} value={faq.id}>
                        <AccordionTrigger className="text-left">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="text-gray-700">{faq.answer}</p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
              
              <div className="mt-8 text-center">
                <p className="mb-4 text-gray-600">Can't find what you're looking for?</p>
                <Button 
                  onClick={() => setActiveTab('ticket')}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  Submit a Support Ticket
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="ticket">
          <TicketSubmissionForm onCancel={() => setActiveTab('faq')} />
        </TabsContent>
        
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>
                Get in touch with our support team directly
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Email Support</h3>
                  <p className="text-gray-600">
                    For general inquiries: <a href="mailto:support@royaltytracker.com" className="text-brand-600 hover:underline">support@royaltytracker.com</a>
                  </p>
                  <p className="text-gray-600 mt-1">
                    For urgent payment issues: <a href="mailto:payments@royaltytracker.com" className="text-brand-600 hover:underline">payments@royaltytracker.com</a>
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">Phone Support</h3>
                  <p className="text-gray-600">
                    Monday to Friday, 9 AM - 5 PM EST:
                  </p>
                  <p className="text-gray-600 font-medium mt-1">
                    +1 (800) 555-1234
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">Mailing Address</h3>
                  <address className="not-italic text-gray-600">
                    Royalty Tracker, Inc.<br />
                    123 Publishing Lane<br />
                    Suite 400<br />
                    New York, NY 10001<br />
                    United States
                  </address>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-gray-600">
                    For fastest response, please submit a support ticket through our system.
                  </p>
                  <Button 
                    onClick={() => setActiveTab('ticket')} 
                    className="mt-4 bg-brand-600 hover:bg-brand-700"
                  >
                    Submit a Ticket
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </MainLayout>
  );
};

export default HelpCenter;
